import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { buildRosterConfigHash, parseRosterConfigHash } from '../lib/rosterConfigHash'
import { useSavedRankingConfigs } from '../hooks/useDraftPrep'
import { useAuth } from '../hooks/useAuth'
import { api } from '../lib/api'
import { toast } from '../components/ui/Toast'
import FantasyMyRankings from '../components/leagues/FantasyMyRankings'
import DraftPrepAdp from '../components/draftPrep/DraftPrepAdp'
import DraftPrepSyncPanel from '../components/draftPrep/DraftPrepSyncPanel'
import InPersonDraftBoard from '../components/draftPrep/InPersonDraftBoard'
import SavedRankings from '../components/draftPrep/SavedRankings'
import MockDraftPage from './MockDraftPage'

const BACKDROP_KEY = 'draft_prep_backdrop_y'
// Remembers the last scoring + roster the user was working in. Without this
// the page remounted at PPR + the default roster every visit, silently
// swapping the user off their own board (a superflex board looks almost
// identical to the default in the roster pill — only the SFLEX chip differs).
const CONFIG_KEY = 'draft_prep_active_config'

const TABS = ['My Rankings', 'ADP', 'Mock Draft', 'In-Person Draft']

const SCORING_OPTIONS = [
  { value: 'half_ppr', label: 'Half-PPR' },
  { value: 'ppr', label: 'PPR' },
  { value: 'standard', label: 'Standard' },
]

const DEFAULT_ROSTER = { qb: 1, rb: 2, wr: 3, te: 1, flex: 1, sflex: 0, k: 1, def: 1 }

const SLOT_LABELS = [
  { key: 'qb', label: 'QB' },
  { key: 'rb', label: 'RB' },
  { key: 'wr', label: 'WR' },
  { key: 'te', label: 'TE' },
  { key: 'flex', label: 'FLEX' },
  { key: 'sflex', label: 'SFLEX' },
  { key: 'k', label: 'K' },
  { key: 'def', label: 'DEF' },
]

function readStoredConfig() {
  try {
    const parsed = JSON.parse(localStorage.getItem(CONFIG_KEY) || 'null')
    if (!parsed?.scoringFormat || !parsed?.rosterSlots) return null
    return parsed
  } catch {
    return null
  }
}

function rosterLabel(slots) {
  return SLOT_LABELS
    .filter((s) => (slots[s.key] || 0) > 0)
    .map((s) => `${slots[s.key]}${s.label}`)
    .join(' / ')
}

export default function DraftPrepPage() {
  const { profile } = useAuth()
  const isAdmin = !!profile?.is_admin
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const initialTab = TABS.findIndex((t) => t.toLowerCase().replace(/\s+/g, '-') === searchParams.get('tab'))
  const [activeTab, setActiveTab] = useState(initialTab >= 0 ? initialTab : 0)
  const [scoringFormat, setScoringFormat] = useState(() => readStoredConfig()?.scoringFormat || 'ppr')
  const [rosterSlots, setRosterSlots] = useState(() => ({ ...DEFAULT_ROSTER, ...(readStoredConfig()?.rosterSlots || {}) }))
  const [showConfig, setShowConfig] = useState(false)
  const [introOpen, setIntroOpen] = useState(false)
  const [mockDraftActive, setMockDraftActive] = useState(false)

  // Backdrop position (admin-tunable, public read)
  const { data: backdropSetting } = useQuery({
    queryKey: ['app-settings', BACKDROP_KEY],
    queryFn: () => api.get(`/admin/app-settings/${BACKDROP_KEY}`),
  })
  const savedBackdropY = backdropSetting?.value?.y ?? 50
  const [backdropY, setBackdropY] = useState(50)
  const [adjustingBackdrop, setAdjustingBackdrop] = useState(false)
  const backdropDragRef = useRef(null)
  useEffect(() => { setBackdropY(savedBackdropY) }, [savedBackdropY])

  const handleBackdropDrag = useCallback((e) => {
    if (e.cancelable) e.preventDefault()
    const ref = backdropDragRef.current
    if (!ref) return
    const clientY = e.touches ? e.touches[0].clientY : e.clientY
    const rect = ref.getBoundingClientRect()
    const pct = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100))
    setBackdropY(pct)
  }, [])

  function startBackdropDrag(e) {
    e.preventDefault()
    handleBackdropDrag(e)
    const onUp = () => {
      document.removeEventListener('mousemove', handleBackdropDrag)
      document.removeEventListener('mouseup', onUp)
      document.removeEventListener('touchmove', handleBackdropDrag)
      document.removeEventListener('touchend', onUp)
    }
    document.addEventListener('mousemove', handleBackdropDrag)
    document.addEventListener('mouseup', onUp)
    document.addEventListener('touchmove', handleBackdropDrag, { passive: false })
    document.addEventListener('touchend', onUp)
  }

  const saveBackdrop = useMutation({
    mutationFn: () => api.put(`/admin/app-settings/${BACKDROP_KEY}`, { value: { y: Math.round(backdropY) } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-settings', BACKDROP_KEY] })
      toast('Backdrop position saved', 'success')
      setAdjustingBackdrop(false)
    },
    onError: (err) => toast(err.message || 'Failed to save', 'error'),
  })

  const configHash = buildRosterConfigHash(rosterSlots)

  // Remember the active board on this device.
  useEffect(() => {
    try {
      localStorage.setItem(CONFIG_KEY, JSON.stringify({ scoringFormat, rosterSlots }))
    } catch {
      // Private mode / quota — the server-side hydrate below still covers us.
    }
  }, [scoringFormat, rosterSlots])

  // First visit on a NEW device has no localStorage, so fall back to the
  // user's most-recently-updated saved board (the API returns them sorted
  // by last_updated). Without this, signing in on another machine drops you
  // onto a default board that isn't the one you've been building.
  const { data: savedConfigs } = useSavedRankingConfigs()
  const hydratedRef = useRef(!!readStoredConfig())
  useEffect(() => {
    if (hydratedRef.current) return
    const top = savedConfigs?.[0]
    if (!top) return
    hydratedRef.current = true
    setScoringFormat(top.scoring_format)
    setRosterSlots({ ...DEFAULT_ROSTER, ...parseRosterConfigHash(top.config_hash) })
  }, [savedConfigs])

  // The board the current scoring + roster resolves to, if it's one the user
  // has actually saved. Surfaced in the config bar so you can tell at a
  // glance which board you're editing — a superflex board and the default
  // differ by a single SFLEX chip, which is far too easy to miss mid-draft.
  const activeSavedConfig = savedConfigs?.find(
    (c) => c.config_hash === configHash && c.scoring_format === scoringFormat,
  )

  function loadSavedConfig({ scoringFormat: sf, rosterSlots: rs }) {
    // An explicit pick from the picker wins over any later hydrate.
    hydratedRef.current = true
    if (sf) setScoringFormat(sf)
    if (rs) setRosterSlots({ ...DEFAULT_ROSTER, ...rs })
  }

  function handleTabChange(i) {
    setActiveTab(i)
    setSearchParams({ tab: TABS[i].toLowerCase().replace(/\s+/g, '-') }, { replace: true })
  }

  function adjustSlot(key, delta) {
    setRosterSlots((prev) => ({
      ...prev,
      [key]: Math.max(0, (prev[key] || 0) + delta),
    }))
  }

  return (
    <div className="relative">
      {/* Hero backdrop */}
      <div
        ref={backdropDragRef}
        className={`absolute inset-x-0 top-0 h-[520px] md:h-[480px] overflow-hidden ${adjustingBackdrop ? 'pointer-events-auto cursor-ns-resize' : 'pointer-events-none'}`}
        style={{ zIndex: adjustingBackdrop ? 30 : 0, touchAction: adjustingBackdrop ? 'none' : 'auto' }}
        onMouseDown={adjustingBackdrop ? startBackdropDrag : undefined}
        onTouchStart={adjustingBackdrop ? startBackdropDrag : undefined}
      >
        <img
          src="/ff-draft-prep-bg.png"
          alt=""
          className={`w-full h-full object-cover ${adjustingBackdrop ? 'opacity-60' : 'opacity-30'}`}
          style={{ objectPosition: `center ${backdropY}%` }}
          draggable={false}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-bg-primary/20 via-bg-primary/40 to-bg-primary" />
        {adjustingBackdrop && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-white/70 text-sm font-medium bg-black/40 px-3 py-1.5 rounded-lg">Drag up or down to reposition</p>
          </div>
        )}
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 py-6 pb-32">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Link to="/leagues" className="text-text-muted hover:text-text-primary">←</Link>
        <h1 className="font-display text-3xl flex-1">Fantasy Football Draft Prep</h1>
        {isAdmin && !adjustingBackdrop && (
          <button
            onClick={() => setAdjustingBackdrop(true)}
            className="text-xs px-2.5 py-1 rounded-lg bg-bg-primary/40 border border-text-primary/20 text-text-secondary hover:text-text-primary hover:bg-bg-primary/60 transition-colors"
          >
            Adjust backdrop
          </button>
        )}
        {isAdmin && adjustingBackdrop && (
          <div className="flex gap-2">
            <button
              onClick={() => { setBackdropY(savedBackdropY); setAdjustingBackdrop(false) }}
              className="text-xs px-2.5 py-1 rounded-lg bg-bg-secondary border border-text-primary/20 text-text-secondary hover:text-text-primary"
            >Cancel</button>
            <button
              onClick={() => saveBackdrop.mutate()}
              disabled={saveBackdrop.isPending}
              className="text-xs px-2.5 py-1 rounded-lg bg-accent text-white font-semibold disabled:opacity-50"
            >{saveBackdrop.isPending ? 'Saving…' : 'Save'}</button>
          </div>
        )}
      </div>

      {/* Intro */}
      <div className={`rounded-xl border border-text-primary/20 backdrop-blur-md p-4 mb-4 mt-20 md:mt-4 transition-colors ${introOpen ? 'bg-bg-primary/15' : 'bg-bg-primary/5'}`}>
        <button
          onClick={() => setIntroOpen(!introOpen)}
          className="w-full flex items-center justify-between gap-3 text-left"
        >
          <p className="text-sm font-bold text-text-primary">
            Preparation is the best way to secure your league championships!
          </p>
          <svg
            className={`w-5 h-5 shrink-0 text-accent transition-transform ${introOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {introOpen && (
          <ul className="space-y-1.5 text-sm text-text-primary/80 mt-3">
            <li className="flex gap-2"><span className="text-accent">•</span><span>Rank every player your way — reorder, search, and filter your own board</span></li>
            <li className="flex gap-2"><span className="text-accent">•</span><span>Bring your board to your in-person draft — mark picks live and see who's still on the board</span></li>
            <li className="flex gap-2"><span className="text-accent">•</span><span>Tune it for your scoring (PPR / Half-PPR / Standard) and roster shape</span></li>
            <li className="flex gap-2"><span className="text-accent">•</span><span>Compare your rankings against ADP and projections side-by-side</span></li>
            <li className="flex gap-2"><span className="text-accent">•</span><span>Sync your rankings to any of your fantasy leagues — edits flow both ways</span></li>
            <li className="flex gap-2"><span className="text-accent">•</span><span>Mock-draft against the field using your rankings and/or ADP rankings</span></li>
          </ul>
        )}
      </div>

      {/* Config bar */}
      <div className="rounded-xl border border-text-primary/20 bg-bg-primary/30 backdrop-blur-md p-3 mb-4">
        {/* Which saved board the current scoring + roster resolves to. Only
            renders for boards the user actually named/customized — an
            unsaved combo intentionally shows nothing rather than a fake label. */}
        {activeSavedConfig && (
          <div className="flex items-center gap-2 mb-2.5 pb-2.5 border-b border-text-primary/10">
            <span className="text-[9px] font-bold text-accent uppercase tracking-wider px-1.5 py-0.5 rounded border border-accent/40 shrink-0">
              Editing
            </span>
            <span className="text-sm font-semibold text-text-primary truncate">
              {activeSavedConfig.name || rosterLabel(rosterSlots)}
            </span>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-text-muted font-semibold">Scoring:</span>
            <div className="flex gap-1">
              {SCORING_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setScoringFormat(opt.value)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                    scoringFormat === opt.value
                      ? 'bg-accent text-white'
                      : 'bg-bg-secondary text-text-secondary hover:bg-white/10'
                  }`}
                >{opt.label}</button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-text-muted font-semibold">Roster:</span>
            <button
              onClick={() => setShowConfig(!showConfig)}
              className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-bg-secondary border border-text-primary/20 text-text-secondary hover:bg-white/10 transition-colors"
            >
              {rosterLabel(rosterSlots)} {showConfig ? '▲' : '▼'}
            </button>
          </div>
        </div>

        {/* Expandable roster editor */}
        {showConfig && (
          <div className="mt-3 pt-3 border-t border-text-primary/10">
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
              {SLOT_LABELS.map((s) => (
                <div key={s.key} className="flex flex-col items-center gap-1">
                  <span className="text-[10px] font-semibold text-text-muted">{s.label}</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => adjustSlot(s.key, -1)}
                      className="w-6 h-6 rounded bg-bg-secondary text-text-muted hover:text-text-primary text-sm flex items-center justify-center"
                    >−</button>
                    <span className="text-sm font-bold text-text-primary w-4 text-center">{rosterSlots[s.key] || 0}</span>
                    <button
                      onClick={() => adjustSlot(s.key, 1)}
                      className="w-6 h-6 rounded bg-bg-secondary text-text-muted hover:text-text-primary text-sm flex items-center justify-center"
                    >+</button>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => { setRosterSlots({ ...DEFAULT_ROSTER }); setShowConfig(false) }}
              className="mt-2 text-[10px] text-text-muted hover:text-text-primary underline"
            >
              Reset to default
            </button>
          </div>
        )}
      </div>

      {/* Sub-tabs. When a mock draft is in progress, non-Mock tabs dim
          so an accidental tap is less likely to yank the user out of
          their pick. Tabs stay tappable — just visually de-emphasized. */}
      <div className="flex gap-1 mb-4 overflow-x-auto">
        {TABS.map((tab, i) => {
          const isMockTab = tab === 'Mock Draft'
          const dim = mockDraftActive && !isMockTab
          return (
            <button
              key={tab}
              onClick={() => handleTabChange(i)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all backdrop-blur-md ${
                activeTab === i
                  ? 'bg-accent text-white'
                  : 'bg-bg-primary/30 border border-text-primary/20 text-text-secondary hover:bg-white/10'
              } ${dim ? 'opacity-30 hover:opacity-60' : ''}`}
            >{tab}</button>
          )
        })}
      </div>

      {/* Tab content */}
      {activeTab === 0 && (
        <div className="space-y-4">
          <SavedRankings
            activeScoringFormat={scoringFormat}
            activeConfigHash={configHash}
            onLoad={loadSavedConfig}
          />
          <FantasyMyRankings draftPrepConfig={{ scoringFormat, configHash }} />
          <DraftPrepSyncPanel configHash={configHash} scoringFormat={scoringFormat} />
        </div>
      )}
      {activeTab === 1 && (
        <DraftPrepAdp scoringFormat={scoringFormat} />
      )}
      {activeTab === 2 && (
        <MockDraftPage
          embedded
          defaultConfig={{ scoringFormat, configHash }}
          onDraftStateChange={setMockDraftActive}
        />
      )}
      {activeTab === 3 && (
        <div className="space-y-4">
          <SavedRankings
            activeScoringFormat={scoringFormat}
            activeConfigHash={configHash}
            onLoad={loadSavedConfig}
          />
          <InPersonDraftBoard scoringFormat={scoringFormat} configHash={configHash} rosterSlots={rosterSlots} />
        </div>
      )}
      </div>
    </div>
  )
}
